import { useEffect, useRef, useState, useCallback } from "react";

interface TurnstileGlobal {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    _tsCallbacks?: (() => void)[];
    _tsTurnstileReady?: boolean;
    onTurnstileLoad?: () => void;
  }
}

// index.html defines window.onTurnstileLoad and window._tsCallbacks before the script tag.
// This hook just needs to enqueue/dequeue from that array.

const SITE_KEY = (import.meta as { env: Record<string, string> }).env.VITE_TURNSTILE_SITE_KEY ?? "";

type TurnstileOptions = {
  mode?: "invisible" | "visible";
  active?: boolean;
};

export function useTurnstile(options?: TurnstileOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scriptBlocked, setScriptBlocked] = useState<boolean>(false);
  const pendingRef = useRef<Array<(t: string) => void>>([]);
  const mode = options?.mode ?? "invisible";
  const active = options?.active ?? true;

  const handleToken = useCallback((t: string) => {
    setToken(t);
    pendingRef.current.forEach(r => r(t));
    pendingRef.current = [];
  }, []);

  useEffect(() => {
    if (!active || !containerRef.current || !SITE_KEY) return;

    function render() {
      if (!containerRef.current || widgetIdRef.current) return;
      setIsLoading(true);
      widgetIdRef.current = window.turnstile!.render(containerRef.current, {
        sitekey: SITE_KEY,
        size: mode === "visible" ? "normal" : "invisible",
        callback: (t: string) => {
          setIsLoading(false);
          setScriptBlocked(false);
          handleToken(t);
        },
        "expired-callback": () => {
          setToken(null);
          // Immediately re-execute so a fresh token is ready for the next submit
          if (widgetIdRef.current) {
            try { window.turnstile?.execute(widgetIdRef.current); } catch { /* ignore */ }
          }
        },
        "error-callback": () => {
          setIsLoading(false);
          setToken(null);
          // Retry after 3 seconds on transient Cloudflare errors
          setTimeout(() => {
            if (widgetIdRef.current) {
              try { window.turnstile?.reset(widgetIdRef.current); window.turnstile?.execute(widgetIdRef.current); } catch { /* ignore */ }
            }
          }, 3_000);
        },
      });
      // Pre-execute immediately so the token is ready before the user submits
      if (mode === "invisible") {
        try { window.turnstile?.execute(widgetIdRef.current!); } catch { /* ignore */ }
      }
    }

    if (window.turnstile) {
      render();
    } else {
      window._tsCallbacks = window._tsCallbacks ?? [];
      window._tsCallbacks.push(render);
      setScriptBlocked(true);
      setIsLoading(false);
    }

    return () => {
      if (widgetIdRef.current) {
        try { window.turnstile?.remove(widgetIdRef.current); } catch { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [active, handleToken, mode]);

  const getToken = useCallback((): Promise<string> => {
    if (!SITE_KEY) return Promise.resolve("");
    if (token) return Promise.resolve(token);
    // If the widget never rendered (script blocked, ad blocker, CSP, etc.)
    // resolve immediately with "" rather than hanging for 30 s. The backend
    // is configured to treat a missing token as a no-op for most routes.
    if (!widgetIdRef.current) return Promise.resolve("");
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(""), 8_000);
      pendingRef.current.push(t => {
        clearTimeout(timeout);
        resolve(t);
      });
      setIsLoading(true);
      try { window.turnstile?.execute(widgetIdRef.current!); } catch { /* ignore */ }
    });
  }, [token]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current) {
      try { window.turnstile?.reset(widgetIdRef.current); } catch { /* ignore */ }
    }
  }, []);

  return { containerRef, token, getToken, reset, isLoading, scriptBlocked };
}
